import { useNavigate } from 'react-router-dom';
import { Grid, Divider } from "@mui/material";
import { Box } from "@mui/system";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NCRFormHeading from '../NCRFormHeading';
import NCRFormLabel from '../NCRFormLabel';
import AILabelBox from '../AILabelBox';
import { handleAIClick } from '../../../Helpers/NCRHelpers';
import NCRSelectField from '../NCRSelectField';
import NCRMultilineTextfields from '../NCRMultilineTextfields';
import { setTab } from '../../../Helpers/helperSlice';
import { useDispatch } from 'react-redux';
import NCRTextField from '../NCRTextField';

export default function PRCEntry({ content, onChange, onAiResponseChange, isAIrrcOpen = null, setAIrrcOpen = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = createTheme({
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  const handleAIClickToGetResponse = async (fieldName) => {
    await handleAIClick(fieldName, content, (value) => {
      onAiResponseChange(fieldName, value);
    });
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box id="processRootCauseSection">
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <NCRFormHeading isRequired={true} tooltipData={
                [
                  { title: "Process Description", description: "Briefly describe the process or operation where the non-conformance occurred." },
                  { title: "Root Cause Identification", description: "Options for common root causes." },
                  { title: "Corrective Actions for Process Root Cause (PRC)", description: "Outline the corrective actions to address and fix the identified Process Root Cause (PRC)." }
                ]
              }>
                Process Root Cause (PRC) Entry
              </NCRFormHeading>
              {content.label === "action" &&
                <div
                  style={{ color: '#2A3DBD', cursor: "pointer", fontWeight: 500, marginTop: '5px' }}
                  onClick={() => {
                    dispatch(setTab(1));
                    navigate(`/ncpro/analysis/${content.formData.ncr_id}`, {
                      state: { fromAction: true }
                    });
                  }}
                >
                  Click here to open RC analysis
                </div>
              }
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: '50%', mr: 4 }}>
                <Grid item xs={4}>
                  <NCRFormLabel sx={{ mb: 1 }}>
                    Process root cause Description:
                  </NCRFormLabel>
                </Grid>
                <Grid item xs={8}>
                  <NCRMultilineTextfields
                    name="process_root_cause_description"
                    value={content.formData.process_root_cause_description || ""}
                    onChange={handleInputChange}
                    disabled={content.inputDisable}
                    error={!!content?.errors?.process_root_cause_description}
                    placeholder={!content.inputDisable ? 'Enter process root cause description' : 'No data entered'}
                  />
                </Grid>
              </Box>
              {!content?.inputDisable && content?.label !== 'edit' &&
                <AILabelBox
                  label="process_root_cause_description"
                  aiResponse={content.aiResponse || {}}
                  handleAIClick={handleAIClickToGetResponse}
                  onChange={onChange}
                />
              }
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <Box sx={{ mb: 2 }}>
                    <Grid container alignItems="center">
                      <Grid item xs={2}>
                        <NCRFormLabel>
                          Root Cause Identification:
                        </NCRFormLabel>
                      </Grid>
                      <NCRSelectField
                        size="medium"
                        name="process_root_cause_identification"
                        value={content.formData.process_root_cause_identification}
                        onChange={handleInputChange}
                        options={content.lookupTypes?.RootCauseIdentification || []}
                        disabled={content.inputDisable}
                        error={!!content?.errors?.process_root_cause_identification}
                      />
                    </Grid>
                  </Box>
                  {content.formData.process_root_cause_identification === 'Other' &&
                    <Box sx={{ mb: 2 }}>
                      <Grid container alignItems="center">
                        <Grid item xs={2}>
                          <NCRFormLabel>Root Cause Identification:</NCRFormLabel>
                        </Grid>
                        <NCRTextField
                          size='small'
                          sx={{ width: '340px' }}
                          multiline
                          name="process_root_cause_identification_other"
                          onChange={handleInputChange}
                          disabled={content.inputDisable}
                          value={content.formData.process_root_cause_identification_other || ''}
                          placeholder="Enter root cause identification"
                          error={!!content?.errors?.process_root_cause_identification_other}
                          maxLength={250}
                        />
                      </Grid>
                    </Box>
                  }
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', }}>
              <Box sx={{ width: '50%', mr: 4 }}>
                <Grid item xs={4}>
                  <NCRFormLabel sx={{ mb: 1 }}>
                    Corrective Actions for Process Root Cause (PRC):
                  </NCRFormLabel>
                </Grid>
                <Grid item xs={8}>
                  <NCRMultilineTextfields
                    name="corrective_actions_process_root_cause"
                    value={content.formData.corrective_actions_process_root_cause || ""}
                    onChange={handleInputChange}
                    disabled={content.inputDisable}
                    error={!!content?.errors?.corrective_actions_process_root_cause}
                    placeholder={!content.inputDisable ? 'Enter corrective actions for process root cause' : 'No data entered'}
                  />
                </Grid>
              </Box>
              {!content?.inputDisable && content?.label !== 'edit' &&
                <AILabelBox
                  label="corrective_actions_process_root_cause"
                  aiResponse={content.aiResponse || {}}
                  handleAIClick={handleAIClickToGetResponse}
                  onChange={onChange}
                />
              }
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', }}>
              {content?.label === 'action' &&
                <AILabelBox
                  is_rrc={true}
                  label="relevant_root_cause"
                  content={content}
                  isAIrrcOpen={isAIrrcOpen}
                  setAIrrcOpen={setAIrrcOpen}
                  aiResponse={content.aiResponse || {}}
                  handleAIClick={handleAIClickToGetResponse}
                  onChange={onChange}
                />
              }
            </Box>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 0.5 }} />
        </Box>
      </ThemeProvider >
    </>
  );
};
